/**
 * MobileTopBar — small-screen header. The SideNav collapses to a bottom tab
 * bar below 768px, which leaves no room for the brand, the theme switch or the
 * secondary destinations, so they live here instead.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useTheme } from '@wlb/shared/theme';
import { BrandLogo } from '../ui/BrandLogo';
import { PopoverMenu } from '../ui/PopoverMenu';
import { MonitorIcon, MoonIcon, MoreIcon, SunIcon } from '../ui/icons';
import { MobileMenuDrawer } from './MobileMenuDrawer';
import './layout.css';

export function MobileTopBar() {
  const { t } = useTranslation();
  const { preference, setPreference, isDark } = useTheme();
  const [themeAnchor, setThemeAnchor] = useState<DOMRect | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const themeToggleRef = useRef<HTMLButtonElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setThemeAnchor(null);
  }, [pathname]);

  const closeThemeMenu = (): void => {
    setThemeAnchor(null);
    themeToggleRef.current?.focus();
  };

  const closeMenu = (): void => {
    setMenuOpen(false);
    menuToggleRef.current?.focus();
  };

  return (
    <header className="mobile-top-bar">
      <Link to="/" className="mobile-top-bar__logo" aria-label={t('home.accessibility.homeLink')}>
        <BrandLogo />
      </Link>

      <div className="mobile-top-bar__actions">
        <button
          ref={themeToggleRef}
          type="button"
          className="mobile-top-bar__button"
          onClick={event => setThemeAnchor(event.currentTarget.getBoundingClientRect())}
          aria-label={t('common.theme.toggle')}
          aria-haspopup="menu"
          aria-expanded={Boolean(themeAnchor)}
        >
          {isDark ? <MoonIcon size={20} /> : <SunIcon size={20} />}
        </button>
        <button
          ref={menuToggleRef}
          type="button"
          className="mobile-top-bar__button"
          onClick={() => setMenuOpen(open => !open)}
          aria-label={t('sidebar.accessibility.openMenu')}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
        >
          <MoreIcon size={20} />
        </button>
      </div>

      {/* The bar is sticky, which makes it a stacking context — its overlays
          would paint under the bottom tab bar without a portal. */}
      {createPortal(
        <>
          <PopoverMenu
            anchorRect={themeAnchor}
            label={t('common.theme.menuLabel')}
            items={[
              { key: 'light', label: t('common.theme.light'), icon: SunIcon, selected: preference === 'light' },
              { key: 'dark', label: t('common.theme.dark'), icon: MoonIcon, selected: preference === 'dark' },
              { key: 'system', label: t('common.theme.system'), icon: MonitorIcon, selected: preference === 'system' },
            ]}
            onClose={closeThemeMenu}
            onSelect={key => {
              setPreference(key);
              closeThemeMenu();
            }}
          />
          {menuOpen && <MobileMenuDrawer onClose={closeMenu} />}
        </>,
        document.body,
      )}
    </header>
  );
}
